import { useState, useEffect, useRef } from "react";
import { analyticsPushFunc, openFacebookShare, openMailShare, openTwitterShare } from "../../../widgetsUtils";
import { WhatsAppShareButton } from "../../../siteWidgets/socialShareButtons/whatsApp/whatsAppShareButton";
import { FrontCopyButton } from "../../../commonComponents/inline/frontCopyButton/frontCopyButton";

export type ShareItemType = "verticalVideo" | "newsFlash" | "rylz";
export type ShareButtonId = "fb" | "wa" | "tw" | "mail" | "copy";

const DEFAULT_SHARE_BUTTONS: ShareButtonId[] = ["fb", "wa", "tw", "copy"];

const SHARE_BUTTON_LABELS: Record<ShareButtonId, string> = {
    fb: "Facebook",
    wa: "WhatsApp",
    tw: "Twitter",
    mail: "Mail",
    copy: "Copy Link",
};

interface VerticalShareAreaProps {
    title: string;
    domain: string;
    isMobile: boolean;
    videoPageId: string;
    copyLinkLabel: string;
    linkCopiedLabel: string;
    shareItemType: ShareItemType
    link?: string;
    articleId?: string;
    analyticShareLabel?: string;
    /** Ordered list of share actions to render. Defaults to fb, wa, tw, copy. */
    shareButtons?: ShareButtonId[];
    /** Show text labels under icons. Defaults to true. */
    showLabels?: boolean;
    /** Close the menu after a share action. Defaults to true. */
    closeOnShareClick?: boolean;
    /** Close the menu when clicking outside. Defaults to false. */
    closeOnOutsideClick?: boolean;
    /** Hide the share trigger while the menu is open (inline expand). Defaults to false. */
    hideTriggerWhenOpen?: boolean;
}

export const VerticalShareArea = (props: VerticalShareAreaProps) => {

    const {
        domain, isMobile, videoPageId, title, copyLinkLabel, linkCopiedLabel, link,
        analyticShareLabel, shareItemType, articleId,
        shareButtons = DEFAULT_SHARE_BUTTONS,
        showLabels = true,
        closeOnShareClick = true,
        closeOnOutsideClick = true,
        hideTriggerWhenOpen = false,
    } = props;
    const [isVisible, setIsVisible] = useState(false);
    const shareRef = useRef(null) as any;

    const linkToShare = link || `${domain}/video/${videoPageId}`;
    const analyticLabel = analyticShareLabel || "vertical_video";
    const analyticItemTitle = getAnalyticItemTitle(shareItemType);
    const itemId = shareItemType === "verticalVideo" ? videoPageId : articleId;
    const showTrigger = !hideTriggerWhenOpen || !isVisible;

    useEffect(() => {
        if (!isVisible || !closeOnOutsideClick) return;

        const onPointerDown = (e: MouseEvent) => {
            if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
                setIsVisible(false);
            }
        };

        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [isVisible, closeOnOutsideClick]);

    const onToggle = async () => {
        if (isMobile) {
            try {
                // @ts-ignore
                await navigator.share({ url: linkToShare });
            } catch (err) {
                console.log("error with sharing function", err);
            }
        } else {
            setIsVisible(!isVisible);
        }
    };

    const onShareBtnClicked = (button: ShareButtonId) => {
        if (closeOnShareClick) {
            setIsVisible(false);
        }

        if (typeof window !== "undefined") {
            switch (button) {
                case "fb":
                    openFacebookShare(linkToShare, "", () => analyticsPushFunc("Facebook", analyticItemTitle, analyticLabel), title, itemId);
                    break;
                case "tw":
                    openTwitterShare(linkToShare, () => analyticsPushFunc("Twitter", analyticItemTitle, analyticLabel), title, itemId);
                    break;
                case "wa":
                    WhatsAppShareButton.openWhatsAppShare(linkToShare, () => analyticsPushFunc("WhatsApp", analyticItemTitle, analyticLabel), title, itemId);
                    break;
                case "mail":
                    openMailShare(title, "", linkToShare, () => analyticsPushFunc("Mail", analyticItemTitle, analyticLabel), analyticItemTitle, undefined);
                    break;
            }
        }
    };

    const renderShareButton = (button: ShareButtonId) => {
        if (button === "copy") {
            return (
                <div key="copy" className="mediaShareItem copy-item">
                    <FrontCopyButton popupText={linkCopiedLabel} title={title} url={linkToShare} />
                    {showLabels && <div className="mediaShareText">{copyLinkLabel}</div>}
                </div>
            );
        }

        const iconClass = button === "fb" ? "facebook" : button === "wa" ? "whatsapp" : button === "tw" ? "twitter" : "mail";

        return (
            <div key={button} className="mediaShareItem" onClick={() => onShareBtnClicked(button)}>
                <div className={`mediaShareIcon ${iconClass}`}></div>
                {showLabels && <div className="mediaShareText">{SHARE_BUTTON_LABELS[button]}</div>}
            </div>
        );
    };

    if (!itemId) return null;

    const shareAreaClass = [
        "shareArea",
        hideTriggerWhenOpen ? "expand-in-place" : "",
        isVisible ? "is-open" : "",
    ].filter(Boolean).join(" ");

    return (
        <div className={shareAreaClass} ref={shareRef}>
            {showTrigger && <div className="shareBtn" onClick={onToggle}></div>}

            {isVisible &&
                <div className="mediaShareWrapper">
                    {shareButtons.map(renderShareButton)}
                </div>
            }
        </div>
    );
};

const getAnalyticItemTitle = (shareItemType: ShareItemType) => {
    switch (shareItemType) {
        case "verticalVideo":
            return "Vertical Video";
        case "newsFlash":
            return "News Flash";
        case "rylz":
            return "Rylz Article";
        default:
            return "";
    }
};
